/**
 * @graphql cc
 */
import type { FC } from 'react';
import React, { memo, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import type { TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { fg } from '@confluence/feature-gating';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { useInlineComments } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import {
	useCommentsDataState,
	type AnnotationStatus,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { inlineCommentGifContent } from '@confluence/inline-comments-common/entry-points/inlineCommentGifContent.ts';
import {
	VIEW_PAGE_COMMENT_BUTTON_EXPERIENCE,
	VIEW_PAGE_DISCOVER_INLINE_COMMENTS_FEATURE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { isElementInViewport } from '@confluence/dom-helpers';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { getLogger } from '@confluence/logger';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { scrollCommentIntoView } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { expVal } from '@confluence/feature-experiments';

import { CommentButtonPlaceholder } from './CommentButtonPlaceholder.tsx';
import CommentButtonQuery, {
	type usePreloadCommentButtonQuery$data as CommentButtonQueryType,
	type usePreloadCommentButtonQuery$variables as CommentButtonQueryVariables,
} from './__generated__/usePreloadCommentButtonQuery.graphql.ts';
import { CommentIcon } from './CommentIcon.tsx';

type CommentButtonQueryInlineComment = NonNullable<
	NonNullable<CommentButtonQueryType['comments']>['nodes']
>[number];

type CommentButtonProps = {
	contentId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	isFabricPage: boolean;
	isDataLoaded?: boolean;
};

type CommentContainerProps = {
	isDialogOpen: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderCommentIconContainer = styled.div<CommentContainerProps>({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	maxWidth: '100%',
	minWidth: '30px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => (props.isDialogOpen ? token('color.background.accent.gray.bolder') : ''),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => (props.isDialogOpen ? token('color.text.inverse') : 'inherit'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		background: (props) =>
			props.isDialogOpen
				? token('color.background.accent.gray.bolder')
				: token('color.background.accent.gray.subtler'),
	},
});

const BUTTON_STYLE_OVERRIDE = {
	padding: `0 ${token('space.050')} 0 ${token('space.025')}`,
};

const logger = getLogger('comment-button');

/* As user scrolls down Content Header goes out of viewport. When user scrolls up slighlty we display
 Header elements in Sticky Wrapper. Using isElementInViewport to set position of Inline dialog
to always be below the Comment Icon */
export const getPlacement = (commentButton) => {
	if (commentButton) {
		if (isElementInViewport(commentButton)) {
			return 'bottom-end';
		} else {
			return 'auto-end';
		}
	} else {
		return 'bottom-end';
	}
};

/*  NOTE: This component is ONLY for the renderer comment button component
 *		  if you want to change the editor comment button behavior you can
 *		  find that code inside of confluence/next/packages/editor-main-buttons/src/MainButtons.tsx
 */
export const CommentButtonComponent: FC<CommentButtonProps> = memo(
	({ contentId, createAnalyticsEvent, isDataLoaded, isFabricPage }) => {
		const intl = useIntl();
		const { showModal } = useDialogs();
		const { Popup } = useChoreographerAssets();
		const { hasContentChanged } = useCommentsContentState();
		const { resetContentChanged } = useCommentsContentActions();
		const { viewProps, selectionProps, hoverProps } = useAnnotations();
		const { setViewProps, setSelectionProps, setHoverProps } = useAnnotationsDispatch();
		const { isCommentsPanelShown, showCommentsPanel, hideCommentsPanel } = useShowCommentsPanel();
		const { removeCommentQueryParams } = useInlineCommentQueryParams();

		// skip this query for fabric page
		const { loading, data, error } = useQueryInterop<
			CommentButtonQueryType,
			CommentButtonQueryVariables
		>(CommentButtonQuery, {
			variables: { pageId: contentId },
			fetchPolicy: isDataLoaded || window.__SSR_RENDERED__ ? 'cache-first' : 'cache-and-network',
			skip: isFabricPage,
		});

		const [{ unresolvedInlineComments: rapUnresolvedInlineComments }] = useInlineComments();
		const { orderedActiveAnnotationIdList } = useCommentsDataState();

		/* Read data from graphql Query if not-fabric */

		const unresolvedInlineCommentAnnotations: string[] = useMemo(
			() =>
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? (orderedActiveAnnotationIdList ?? []).map(
							(annotation: AnnotationStatus) => annotation.threadKey,
						)
					: (rapUnresolvedInlineComments ?? []),
			[orderedActiveAnnotationIdList, rapUnresolvedInlineComments],
		);

		const inlineCommentsCount = isFabricPage
			? unresolvedInlineCommentAnnotations.length
			: data?.comments?.count || 0;

		const unresolvedInlineComments = isFabricPage
			? unresolvedInlineCommentAnnotations
			: data?.comments?.nodes || [];

		const [isDialogOpen, setDialogState] = useState(false);
		const [isInlineCommentShown, setShowInlineComments] = useState(false);
		const { newCommentHighlight, activeHighlight } = useInlineCommentsContext();

		const experienceTracker = useContext(ExperienceTrackerContext);

		const inlineCommentRef = useRef<HTMLElement | null>(null);
		const commentButtonRef = useRef<HTMLDivElement | null>(null);
		const isFirstClickRef = useRef<boolean>(true);
		const emitter = getRendererAnnotationEventEmitter();

		// handle button click for legacy editor
		useEffect(() => {
			if ((!isFabricPage ? data : true) && isDataLoaded && isFirstClickRef.current) {
				//only call this if it hasn't been clicked before
				handleCommentButtonClick();
				isFirstClickRef.current = false;
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data, isDataLoaded]);

		useEffect(() => {
			return () => {
				//Setting to null, since when the component re-renders (when a New comment is added), we want to query the DOM again to get the First Inline comment by position.
				inlineCommentRef.current = null;
			};
			/**
			 * Have to explicitly disable the eslint rule to ensure the value gets reset to null
			 * Saving an inline comment will trigger a re-fetch of `CommentButtonQuery` and a re-render of this component.
			 */
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [inlineCommentRef.current]);

		useEffect(() => {
			experienceTracker.start({
				name: VIEW_PAGE_COMMENT_BUTTON_EXPERIENCE,
				id: contentId,
				attributes: {
					framework: isFabricPage
						? InlineCommentFramework.ANNOTATION_PROVIDER
						: InlineCommentFramework.REACT,
				},
			});
		}, [experienceTracker, contentId, isFabricPage]);

		//Checks the DOM for the first Inline Comment by position with class valid.
		const getFirstMarkerRefByPosition = () => {
			//checks for both Fabric and Legacy annotations
			const annotations: HTMLElement[] = [].slice.call(
				document.querySelectorAll(
					`[data-mark-annotation-type="inlineComment"],.inline-comment-marker`,
				),
			);

			if (isFabricPage) {
				return (
					annotations.find(
						(item) => item.getAttribute('data-mark-annotation-state') === 'active',
					) || null
				);
			}

			return annotations.find((item) => item.classList.contains('inline-highlight')) || null;
		};

		const getClass = () => 'inline-highlight';

		const renderFirstInlineComment = (elem) => {
			// Handles case where when User clicks Icon and annotations are not yet in the DOM.
			if (!elem) {
				toggleDialog();
			} else if (elem && elem.classList.contains(getClass())) {
				/**
				 * Checking class valid for edge case scenario in which User visits a page with a Single Inline Comment
				 * and then deletes or resolves a Inline Comment.
				 * Deleting comment removes class valid from the annotation
				 */
				if (!elem.classList.contains('active-highlight')) {
					elem.click();
				}

				if (elem.classList.contains('active-highlight')) {
					scrollCommentIntoView({ commentElement: elem, isFabricPage });
				}
			} else {
				/**
				 * Does another check to see if there are any valid inline Comments on the Page.
				 * Handles edge case where we have  multiple Inline comments on the Page and User deletes the first One.
				 * In that case check the DOM to find the Next valid inline Comment and Render
				 * If no valid Inline comment is found it will be handled by above logic
				 */
				renderFirstInlineComment(findFirstInlineComment());
			}
		};

		const findFirstInlineComment = () => {
			const firstInlineCommentByPosition = getFirstMarkerRefByPosition();

			let markerRef =
				firstInlineCommentByPosition &&
				(firstInlineCommentByPosition.getAttribute('data-id') ||
					firstInlineCommentByPosition.getAttribute('data-ref'));

			if (isFabricPage) {
				markerRef = firstInlineCommentByPosition && firstInlineCommentByPosition.getAttribute('id');
			}

			//Check against graphql response to make sure data integrity between DOM and back-end
			const isAnnotationInAnnotationList =
				(markerRef &&
					Boolean(
						unresolvedInlineComments.find((item) => {
							if (isFabricPage) {
								return item === markerRef;
							} else {
								const location = (item as CommentButtonQueryInlineComment)?.location;
								return location?.inlineMarkerRef === markerRef;
							}
						}),
					)) ||
				false;

			if (isAnnotationInAnnotationList) {
				//store using useRef to avoid multiple function calls.
				inlineCommentRef.current = firstInlineCommentByPosition;
				return firstInlineCommentByPosition;
			}

			return null;
		};

		const toggleDialog = () => {
			setDialogState(!isDialogOpen);
			fireScreenEvent();

			experienceTracker.succeed({
				name: VIEW_PAGE_DISCOVER_INLINE_COMMENTS_FEATURE_EXPERIENCE,
				attributes: { pageHasInlineComments: false },
			});
		};

		//Screen Event when we display Dialog to show Users how to add Inline comments
		const fireScreenEvent = () => {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'commentsOnboardingScreen',
					attributes: {
						type: 'header-button',
					},
				},
			}).fire();
		};

		const fireUIEvent = (pageHasInlineComments, shouldShow) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'inlineCommentsIcon',
					attributes: {
						pageHasInlineComments,
						icontype: 'header',
						context: 'default',
						newState: shouldShow ? 'showComments' : 'hideComments',
					},
					objectType: 'page',
					objectId: contentId,
					source: 'viewPageScreen',
				},
			}).fire();
		};

		const checkUnsavedAndHandleCommentButtonClick = () => {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						handleCommentButtonClick();
					},
				});
			} else {
				handleCommentButtonClick();
			}
		};

		const handleCommentButtonClick = () => {
			if (isFabricPage) {
				if (isCommentsPanelShown) {
					void hideCommentsPanel();
				} else {
					// FABRIC PAGES
					// Close the view component if it's open
					if (viewProps) {
						// Inform the Renderer to de-select the annotation
						emitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
						emitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
						// Remove the display of the comment
						setViewProps(null);
					}

					if (selectionProps) {
						selectionProps.onClose();
						setSelectionProps(null);
					}

					if (hoverProps) {
						hoverProps.onClose();
						setHoverProps(null);
					}

					// LEGACY PAGES
					if (newCommentHighlight || activeHighlight) {
						// Close the legacy inline comment panel
						window.dispatchEvent(new Event('closeLegacyInlineCommentSidebar'));
					}

					showCommentsPanel();
				}

				return;
			}

			experienceTracker.start({
				name: VIEW_PAGE_DISCOVER_INLINE_COMMENTS_FEATURE_EXPERIENCE,
				attributes: {
					framework: isFabricPage
						? InlineCommentFramework.ANNOTATION_PROVIDER
						: InlineCommentFramework.REACT,
				},
			});

			const pageHasUnresolvedInlineComments = Boolean(
				inlineCommentsCount && unresolvedInlineComments.length > 0,
			);

			if (pageHasUnresolvedInlineComments) {
				try {
					const firstInlineComment = inlineCommentRef.current || findFirstInlineComment();
					if (isFabricPage && firstInlineComment?.id) {
						removeCommentQueryParams();
						const commentElement = document.getElementById(`${firstInlineComment?.id}`);
						emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
							annotationIds: [firstInlineComment?.id],
							eventTarget: commentElement,
						});
						scrollCommentIntoView({ commentElement, isFabricPage: true });
					} else {
						renderFirstInlineComment(firstInlineComment);
					}

					// Fire UI event for button click in view mode with inline comments
					fireUIEvent(pageHasUnresolvedInlineComments, !isInlineCommentShown);
					setShowInlineComments(!isInlineCommentShown);
				} catch (error) {
					experienceTracker.fail({
						name: VIEW_PAGE_DISCOVER_INLINE_COMMENTS_FEATURE_EXPERIENCE,
						error,
						attributes: { pageHasInlineComments: true },
					});
					logger.error`An Error occured when computing to search for first Unresolved Inline comment`;
				}
			} else {
				// Fire UI event for button click without inline comments
				fireUIEvent(pageHasUnresolvedInlineComments, !isDialogOpen);
				toggleDialog();
			}
		};

		if (loading) {
			return <CommentButtonPlaceholder style={BUTTON_STYLE_OVERRIDE} />;
		} else if (error && isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			//Abort Experience for anonymous users and don't display Comment Button
			// We have a known error with Comments API that does not handle anonymous Users
			//https://product-fabric.atlassian.net/browse/WS-1759
			experienceTracker.abort({
				name: VIEW_PAGE_COMMENT_BUTTON_EXPERIENCE,
				reason: 'Anonymous user',
			});
			return null;
		}

		const getCommentIcon = (triggerProps: TriggerProps) => {
			return (
				<HeaderCommentIconContainer
					isDialogOpen={isDialogOpen}
					data-cy="comment-button"
					ref={commentButtonRef}
					data-testid={contentId}
				>
					<CommentIcon
						triggerProps={triggerProps}
						intl={intl}
						handleCommentButtonClick={
							fg('confluence_frontend_editor_annotation_manager')
								? checkUnsavedAndHandleCommentButtonClick
								: handleCommentButtonClick
						}
					/>
				</HeaderCommentIconContainer>
			);
		};

		return (
			<ErrorBoundary attribution={Attribution.COLLABORATION}>
				<Popup
					messageType="engagement"
					messageId="comment-button-component"
					isOpen={isDialogOpen}
					content={inlineCommentGifContent}
					trigger={(triggerProps) => getCommentIcon(triggerProps)}
					placement={getPlacement(commentButtonRef.current)}
					onClose={() => {
						toggleDialog();
					}}
					shouldRenderToParent
				/>
				<ExperienceSuccess name={VIEW_PAGE_COMMENT_BUTTON_EXPERIENCE} />
				{/*
    In case of graphql Error we will still display the CommentButton since the Component
    handles displaying the GIF showing Users how to add Inline Comments.
    We will call ErrorDisplay to send unhandled Errors
  */}
				{error && <ErrorDisplay error={error} />}
			</ErrorBoundary>
		);
	},
);

export const CommentButton = withAnalyticsEvents()(CommentButtonComponent);
